import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useDashboardData } from '../hooks/useDashboardData';
import { useUnitTelemetry } from '../hooks/useUnitTelemetry';
import { HistoricChart } from '../components/dashboard/HistoricChart';
import { UnitType, UnitStatus } from '../types/index';
import { useMetricsCalculator } from '../hooks/useMetricsCalculator';
import type { RollingWindow } from '../hooks/useMetricsCalculator';
import {
  getBatteryCapacity,
  computeWaterDepthCm,
  computeTankVolumeLiters,
  computeTankCapacityLiters,
  computePercentFilled,
  computeVolumeChangeLiters,
  computeChargeRate,
  waterReadingGap,
} from '@shared/metrics';
import { AlertConfigDialog } from '../components/dashboard/AlertConfigDialog';
import type { AlertConfig } from '../hooks/useUnitAlerts';
import { SearchableSelect } from '../components/layout/SearchableSelect';
import { api, ApiError } from '../utils/api';
import './UnitData.css';

function formatTimeAgo(dateString: string): string {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 0) return 'Just now'; // Handle clock drift

  if (seconds < 60) {
    return `${seconds} second${seconds === 1 ? '' : 's'} ago`;
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }

  const days = Math.floor(hours / 24);
  if (days < 30) {
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    return `${months} month${months === 1 ? '' : 's'} ago`;
  }

  const years = Math.floor(months / 12);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

type TimeRange = '24h' | '7d' | '30d' | '3m' | '1y';
type RefreshPhase = 'idle' | 'sending' | 'waiting';

/** Lookback for the summary cards, in hours (30 days). */
const SUMMARY_WINDOW_HOURS = 30 * 24;

/** Mirrors the server's per-unit refresh cooldown (REFRESH_COOLDOWN_MS). */
const REFRESH_COOLDOWN_MS = 60_000;

/** Device roles that can actually receive a cloud command. LoRa leaves cannot. */
const CLOUD_COMMANDABLE_ROLES = ['cellular', 'gateway'];

export function UnitData() {
  const { unitId } = useParams<{ unitId: string }>();
  const navigate = useNavigate();
  const [volumeWindow, setVolumeWindow] = useState<RollingWindow>('24h');
  const [alertConfigOpen, setAlertConfigOpen] = useState<{metric: AlertConfig['metric'], label: string} | null>(null);

  const { units, sites, isSuperAdmin, userRoles, isLoading: dashboardLoading } = useDashboardData();

  // Find active unit (fallback to first unit if not in URL, or let them select)
  const activeUnit = units.find(u => u.unitId === unitId) ?? units[0];
  const isMobile = activeUnit?.unitType === UnitType.MobileStation;

  const hasSiteAdminRole = userRoles?.some((role: { status?: string; SK?: string; role?: string }) =>
    role.status === 'active' && Boolean(role.SK?.includes(`SITE#${activeUnit?.siteId}`)) && Boolean(role.role && ['LocalAdmin', 'SiteAdmin'].includes(role.role)),
  );
  const hasOrgAdminRole = userRoles?.some((role: { status?: string; SK?: string; role?: string }) =>
    role.status === 'active' && role.SK === `ROLE#TENANT#${activeUnit?.tenantId}` && role.role === 'OrgAdmin',
  );
  const canAccessSettings = isSuperAdmin || hasSiteAdminRole || hasOrgAdminRole;

  // Chart view states
  const [selectedChartMetric, setSelectedChartMetric] = useState('waterVolume');
  const [timeframe, setTimeframe] = useState<TimeRange>('7d');
  const [pivotDate, setPivotDate] = useState<Date>(new Date());
  /** True while the chart is showing the most recent window. */
  const [atLatest, setAtLatest] = useState(true);

  // Offline-alert subscription (F-A) — opt-in, per current user + unit.
  const [offlineSubscribed, setOfflineSubscribed] = useState(false);
  const [offlineBusy, setOfflineBusy] = useState(false);
  // Desired enabled value awaiting the "apply to all?" yes/no decision.
  const [pendingOffline, setPendingOffline] = useState<boolean | null>(null);
  const [refreshPhase, setRefreshPhase] = useState<RefreshPhase>('idle');
  const [refreshIssuedAt, setRefreshIssuedAt] = useState<string | null>(null);
  /** Locally observed end of the cooldown; the server remains the authority. */
  const [refreshCooldownUntil, setRefreshCooldownUntil] = useState(0);
  const [refreshError, setRefreshError] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!activeUnit?.unitId) return;
    api.get('/api/v1/users/me/offline-alerts')
      .then(res => {
        if (!cancelled) setOfflineSubscribed((res?.unitIds ?? []).includes(activeUnit.unitId));
      })
      .catch(() => { /* leave unchecked on failure */ });
    return () => { cancelled = true; };
  }, [activeUnit?.unitId]);

  const commitOffline = async (applyToAll: boolean) => {
    if (pendingOffline === null || !activeUnit?.unitId) return;
    const enabled = pendingOffline;
    setOfflineBusy(true);
    try {
      await api.put(`/api/v1/units/${activeUnit.unitId}/offline-alerts`, { enabled, applyToAll });
      setOfflineSubscribed(enabled);
    } catch {
      /* keep prior state on failure */
    } finally {
      setOfflineBusy(false);
      setPendingOffline(null);
    }
  };

  // Reset states when active unit changes
  useEffect(() => {
    setSelectedChartMetric('waterVolume');
    setPivotDate(new Date());
    setAtLatest(true);
    setRefreshPhase('idle');
    setRefreshIssuedAt(null);
    setRefreshError('');
    setRefreshCooldownUntil(0);
  }, [activeUnit?.unitId]);

  // Reset pivot when timeframe changes
  useEffect(() => {
    setPivotDate(new Date());
    setAtLatest(true);
  }, [timeframe]);

  // Helper to change unit from selector
  const handleUnitChange = (val: string) => {
    if (val) {
      navigate(`/dashboard/units/${val}`);
    }
  };

  // Helper to calculate start/end based on pivot date and timeframe
  const getInterval = (pivot: Date, tf: string) => {
    const end = new Date(pivot);
    const start = new Date(pivot);
    switch (tf) {
      case '24h':
        start.setHours(start.getHours() - 24);
        break;
      case '7d':
        start.setDate(start.getDate() - 7);
        break;
      case '30d':
        start.setDate(start.getDate() - 30);
        break;
      case '3m':
        start.setMonth(start.getMonth() - 3);
        break;
      case '1y':
        start.setFullYear(start.getFullYear() - 1);
        break;
      default:
        start.setHours(start.getHours() - 24);
    }
    return { start, end };
  };

  const { start: intervalStart, end: intervalEnd } = getInterval(pivotDate, timeframe);

  const telemetryRange = useMemo(() => ({
    start: intervalStart.toISOString(),
    end: intervalEnd.toISOString()
  }), [intervalStart, intervalEnd]);

  // Chart series: bound to the range the user is currently browsing.
  const {
    telemetry: telemetryData,
    isLoading: telemetryLoading,
    refetch: refetchTelemetry,
  } = useUnitTelemetry(activeUnit?.unitId, telemetryRange);

  // Summary cards: always the unit's *current* state, independent of where the
  // chart is paged to. Paging back through history must not blank the KPIs.
  // 30 days covers the widest rolling window offered by the Amount Change select.
  const {
    telemetry: summaryTelemetry,
    refetch: refetchSummary,
  } = useUnitTelemetry(activeUnit?.unitId, SUMMARY_WINDOW_HOURS);

  /**
   * Cooldown left at the moment of the call. Read on click only — the page
   * deliberately has no ticking countdown, so nothing here re-renders per second.
   */
  const refreshCooldownRemainingMs = () => {
    const serverRefreshMs = Date.parse(activeUnit?.lastRefreshCommandAt ?? '');
    const serverCooldownUntil = Number.isFinite(serverRefreshMs)
      ? serverRefreshMs + REFRESH_COOLDOWN_MS
      : 0;
    return Math.max(refreshCooldownUntil, serverCooldownUntil) - Date.now();
  };

  const cooldownMessage = (remainingMs: number) =>
    `This unit was updated recently. Try again in ${Math.max(1, Math.ceil(remainingMs / 1000))}s.`;

  useEffect(() => {
    if (refreshPhase !== 'waiting' || !refreshIssuedAt || !activeUnit?.unitId) return;

    let cancelled = false;
    let timer: number | undefined;
    const issuedMs = Date.parse(refreshIssuedAt);
    const deadline = Date.now() + 90_000;
    const pollStart = new Date(issuedMs - 24 * 60 * 60 * 1000).toISOString();

    const poll = async () => {
      if (cancelled) return;
      const rows = await refetchTelemetry({
        start: pollStart,
        // The chart's normal range end is frozen at pivotDate. This deliberately
        // moves on every poll (with clock-skew headroom for the device-timestamp SK).
        end: new Date(Date.now() + 5 * 60 * 1000).toISOString(),
      });
      if (cancelled) return;

      const receivedAfterCommand = rows.some(row => {
        const ingestedMs = typeof row.ingestedAt === 'number'
          ? row.ingestedAt
          : Date.parse(row.ingestedAt ?? '');
        return Number.isFinite(ingestedMs) && ingestedMs >= issuedMs;
      });
      if (receivedAfterCommand) {
        setRefreshPhase('idle');
        setRefreshIssuedAt(null);
        // The new reading landing on the page is the success signal; no banner.
        // The summary cards run their own query, so pull the new reading in too.
        void refetchSummary();
        // Return ownership to the normal chart query, now with a fresh range end.
        setPivotDate(new Date());
        setAtLatest(true);
        return;
      }
      if (Date.now() >= deadline) {
        setRefreshPhase('idle');
        setRefreshIssuedAt(null);
        setRefreshError('No response yet — the unit may be out of coverage.');
        return;
      }
      timer = window.setTimeout(poll, 5000);
    };

    timer = window.setTimeout(poll, 5000);
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [refreshPhase, refreshIssuedAt, activeUnit?.unitId, refetchTelemetry, refetchSummary]);

  const handleRefreshNow = async () => {
    if (!activeUnit?.unitId || refreshPhase !== 'idle') return;

    // Cooldown is checked on click and reported as a one-shot message. The
    // server enforces the same window, so a stale client clock only costs a 429.
    const remainingMs = refreshCooldownRemainingMs();
    if (remainingMs > 0) {
      setRefreshError(cooldownMessage(remainingMs));
      return;
    }

    setRefreshPhase('sending');
    setRefreshError('');
    try {
      const result: { commandId: string; issuedAt: string; expiresAt: string } =
        await api.post(`/api/v1/units/${activeUnit.unitId}/refresh`);
      const issuedMs = Date.parse(result.issuedAt);
      setRefreshIssuedAt(result.issuedAt);
      setRefreshCooldownUntil((Number.isFinite(issuedMs) ? issuedMs : Date.now()) + REFRESH_COOLDOWN_MS);
      setRefreshPhase('waiting');
    } catch (error) {
      setRefreshPhase('idle');
      if (error instanceof ApiError && error.status === 429) {
        const retryAfter = Number(error.data?.retryAfterSeconds);
        const retryMs = (Number.isFinite(retryAfter) ? retryAfter : 60) * 1000;
        setRefreshCooldownUntil(Date.now() + retryMs);
        setRefreshError(cooldownMessage(retryMs));
        return;
      }
      if (error instanceof ApiError && error.status === 503) {
        // A broker error still consumes the server-side cooldown by design.
        setRefreshCooldownUntil(Date.now() + REFRESH_COOLDOWN_MS);
        setRefreshError('The command could not be published. Try again shortly.');
        return;
      }
      setRefreshError(error instanceof Error ? error.message : 'Could not request an update.');
    }
  };

  // Retrieve parent site
  const parentSite = activeUnit ? sites.find(s => s.id === activeUnit.siteId) : null;

  // Summary metrics track live unit state, not the browsed chart range.
  const metrics = useMetricsCalculator(activeUnit, summaryTelemetry, volumeWindow);


  // Tank capacity, via the shared helper so the denominator here can't drift
  // from the litres the rest of the app computes.
  const volume = {
    total: activeUnit?.unitType === UnitType.MobileStation
      ? Math.round(computeTankCapacityLiters(activeUnit) ?? 0)
      : 0,
  };


  // Find current voltage & calculate filled percentage (live state, not the chart range)
  const latestTelemetry = summaryTelemetry && summaryTelemetry.length > 0
    ? [...summaryTelemetry].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0]
    : null;
  const currentVoltage = latestTelemetry ? latestTelemetry.voltage : null;

  // Shared percent-filled, so this page agrees with the Sites list and the map.
  // (They diverged for horizontal tanks, where depth-fraction ≠ volume-fraction.)
  const filledPercentRaw = metrics.waterDepthCm === null
    ? null
    : computePercentFilled(metrics.waterDepthCm, activeUnit);
  const filledPercent = filledPercentRaw === null ? null : Math.round(filledPercentRaw);

  // When a reading can't be turned into a depth, say why rather than leaving
  // the user staring at an unexplained "N/A".
  const waterGapNote = (() => {
    if (!latestTelemetry || activeUnit?.unitType !== UnitType.MobileStation) return null;
    switch (waterReadingGap(activeUnit, latestTelemetry.measurement)) {
      case 'above-tank':
        return 'The sensor reported no usable distance on its last reading, so the water level is unknown. This is usually a sensor or wiring fault rather than a settings problem.';
      case 'below-floor':
        return "The last reading puts the water below the bottom of the tank. Check the tank height and the sensor's mount height in Unit Settings.";
      default:
        return null;
    }
  })();

  const handleOpenAlerts = (metric: AlertConfig['metric'], label: string) => {
    setAlertConfigOpen({ metric, label });
  };

  const renderMetricCard = (
    label: string, 
    value: React.ReactNode, 
    metricKey: AlertConfig['metric'],
    extraHeader?: React.ReactNode,
    isLarge?: boolean
  ) => (
    <div className={`flat-metric-card ${isLarge ? 'flat-metric-card--large' : ''}`}>
      <div className="metric-card-header">
        <span className="metric-label">{label}</span>
        {extraHeader}
        <div className="metric-menu">
          {/* Every card renders one of these, so it needs a name of its own —
              otherwise the page exposes seven buttons all called "⋯". */}
          <button
            type="button"
            className="metric-menu-btn"
            aria-label={`Configure alerts for ${label}`}
            title={`Configure alerts for ${label}`}
            onClick={() => handleOpenAlerts(metricKey, label)}
          >
            <span aria-hidden="true">⋯</span>
          </button>
        </div>
      </div>
      <span className={`metric-value ${isLarge ? '' : 'text-small'}`}>{value}</span>
    </div>
  );

  // Chart options mapping
  const chartMetricOptions = [
    { value: 'waterVolume', label: 'Water Amount (L)' },
    { value: 'filledPercent', label: 'Filled Percentage (%)' },
    { value: 'volumeChange', label: 'Amount Change (L)' },
    { value: 'waterDepth', label: 'Water Depth (cm)' },
    { value: 'batteryCapacity', label: 'Battery Percentage (%)' },
    { value: 'voltage', label: 'Battery Voltage (V)' },
    { value: 'chargeRate', label: 'Battery Charge/Discharge Rate (V/hr)' },
  ];

  const selectedMetricOpt = chartMetricOptions.find(o => o.value === selectedChartMetric);
  const chartMetricLabel = selectedMetricOpt ? selectedMetricOpt.label.split(' (')[0] : '';
  const chartMetricUnit = selectedMetricOpt ? selectedMetricOpt.label.match(/\(([^)]+)\)/)?.[1] || '' : '';

  // Calculate historical telemetry points mapped for the chart
  const chartData = useMemo(() => {
    if (!telemetryData || telemetryData.length === 0 || !activeUnit) return [];

    // Sort telemetry chronologically ascending for line plotting
    const sorted = [...telemetryData].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    return sorted.map((pt, index) => {
      const timestampMs = new Date(pt.timestamp).getTime();
      const voltage = pt.voltage;
      const maxV = activeUnit.maxBatteryVoltage || 4.2;
      const batteryCapacity = getBatteryCapacity(voltage, maxV);
      
      // Water depth & volume (shared metric math). pt.measurement is mm.
      const waterDepth = computeWaterDepthCm(pt.measurement, activeUnit);
      const waterVolume = waterDepth === null ? null : computeTankVolumeLiters(waterDepth, activeUnit);

      // Filled percentage via the shared helper, so the charted series matches
      // the card above it (they differ for horizontal tanks).
      const filledPercent = waterDepth === null ? null : computePercentFilled(waterDepth, activeUnit);

      // Amount change vs the preceding chronological point (shared math).
      let volumeChange: number | null = null;
      if (waterVolume !== null && index > 0) {
        const prevPt = sorted[index - 1];
        const prevDepth = computeWaterDepthCm(prevPt.measurement, activeUnit);
        const prevVolume = prevDepth === null ? null : computeTankVolumeLiters(prevDepth, activeUnit);
        if (prevVolume !== null) {
          volumeChange = computeVolumeChangeLiters(waterVolume, prevVolume);
        }
      }

      // Charge rate vs the preceding chronological point (shared math).
      let chargeRate = 0;
      if (index > 0) {
        const prevPt = sorted[index - 1];
        chargeRate = computeChargeRate(
          { voltage, measurement: pt.measurement, timestamp: pt.timestamp },
          { voltage: prevPt.voltage, measurement: prevPt.measurement, timestamp: prevPt.timestamp },
        );
      }

      return {
        timestamp: pt.timestamp,
        timestampMs,
        waterVolume,
        filledPercent,
        volumeChange,
        waterDepth,
        batteryCapacity,
        voltage,
        chargeRate
      };
    });
  }, [telemetryData, activeUnit, volume.total]);

  // Format YYYY-MM-DD HH:mm:ss for selected interval
  const formatDateTime = (date: Date) => {
    const Y = date.getFullYear();
    const M = String(date.getMonth() + 1).padStart(2, '0');
    const D = String(date.getDate()).padStart(2, '0');
    const h = String(date.getHours()).padStart(2, '0');
    const m = String(date.getMinutes()).padStart(2, '0');
    const s = String(date.getSeconds()).padStart(2, '0');
    return `${Y}-${M}-${D} ${h}:${m}:${s}`;
  };

  const intervalStartText = formatDateTime(intervalStart);
  const intervalEndText = formatDateTime(intervalEnd);
  const timeframeText = `${intervalStartText} - ${intervalEndText}`;

  // Interval navigation controls
  const handlePrevInterval = () => {
    setAtLatest(false);
    setPivotDate(currentPivot => {
      const { start } = getInterval(currentPivot, timeframe);
      return start;
    });
  };

  const handleNextInterval = () => {
    setPivotDate(currentPivot => {
      const { start } = getInterval(currentPivot, timeframe);
      const durationMs = currentPivot.getTime() - start.getTime();
      const nextPivot = new Date(currentPivot.getTime() + durationMs);
      const now = new Date();
      if (nextPivot >= now) {
        setAtLatest(true);
        return now;
      }
      return nextPivot;
    });
  };

  // Tracked explicitly rather than derived from the wall clock: the page
  // re-renders every second, so a "within N seconds of now" comparison would
  // quietly re-enable this button while the user sits on the newest window.
  const isLatest = atLatest;

  if (dashboardLoading || !activeUnit) {
    return (
      <div className="page unit-data-page">
        <div className="units-header-row">
          <div className="units-header-left">
            <h1 className="page__title">Unit Metrics</h1>
          </div>
        </div>
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
          Loading unit details...
        </div>
      </div>
    );
  }

  return (
    <div className="page unit-data-page">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumbs">
        <Link to="/dashboard" className="breadcrumb-link hyperlink">Dashboard</Link>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Unit Metrics</span>
      </div>

      <div className="units-header-row">
        <div className="units-header-left">
          <h1 className="page__title">Unit Metrics</h1>
          <SearchableSelect
            id="unit-select"
            value={activeUnit.unitId}
            onChange={handleUnitChange}
            options={units
              .filter(u => u.status === UnitStatus.Adopted)
              .map(u => {
                const site = sites.find(s => s.id === u.siteId);
                return {
                  value: u.unitId,
                  label: `${u.nodeName} (ID: ${u.nodeId})`,
                  subLabel: site ? site.name : undefined
                };
              })
            }
            placeholder="Switch Unit..."
            excludeSubLabelFromSearch={true}
            highlightSubLabel={true}
          />
        </div>
      </div>

      {/* Active Unit Info Sub-Header */}
      <div className="unit-meta-header">
        {/* Each label+value is one unit so a narrow viewport wraps between the
            facts rather than through the middle of "Test Site" or "47 minutes ago". */}
        <p className="unit-meta-subtitle">
          <span className="meta-item">
            Site:{' '}
            {parentSite ? (
              <Link to={`/dashboard/sites?siteId=${parentSite.id}`} className="hyperlink font-semibold">
                {parentSite.name}
              </Link>
            ) : (
              'Unknown'
            )}
          </span>
          <span className="meta-separator" aria-hidden="true">|</span>
          <span className="meta-item">
            Node ID: <span className="font-semibold">{activeUnit.nodeId}</span>
          </span>
          <span className="meta-separator" aria-hidden="true">|</span>
          <span className="meta-item">
            Last Seen: <span className="font-semibold">{formatTimeAgo(activeUnit.lastSeenAt)}</span>
          </span>
        </p>

        <label className="offline-alert-toggle" style={{ cursor: offlineBusy ? 'wait' : 'pointer' }}>
          <input
            type="checkbox"
            checked={offlineSubscribed}
            disabled={offlineBusy}
            onChange={e => setPendingOffline(e.target.checked)}
            style={{ width: '1.1rem', height: '1.1rem', cursor: offlineBusy ? 'wait' : 'pointer' }}
          />
          Notify me if this unit stops reporting
        </label>

        {/* Only cellular-linked devices can be reached by a cloud command; a LoRa
            leaf answers to its gateway, not to us. */}
        {CLOUD_COMMANDABLE_ROLES.includes(activeUnit.role ?? '') && (
          <div className="refresh-now-control">
            <button
              type="button"
              className="refresh-now-button"
              onClick={handleRefreshNow}
              disabled={refreshPhase !== 'idle'}
            >
              {refreshPhase === 'sending'
                ? 'Sending…'
                : refreshPhase === 'waiting'
                  ? 'Waiting for reading…'
                  : 'Update Now'}
            </button>
            {refreshError && (
              <span className="refresh-now-error" role="alert">{refreshError}</span>
            )}
          </div>
        )}

        {canAccessSettings && parentSite && (
          <div className="unit-meta-actions">
            <Link
              to={`/dashboard/sites?siteId=${parentSite.id}&tab=units&unitId=${activeUnit.unitId}`}
              className="hyperlink text-small font-semibold"
            >
              Unit Settings
            </Link>
          </div>
        )}
      </div>

      {/* Offline-alert apply-to-all confirmation (F-A) */}
      {pendingOffline !== null && (
        <div
          role="dialog"
          aria-modal="true"
          className="offline-dialog-overlay"
          onClick={() => !offlineBusy && setPendingOffline(null)}
        >
          <div className="offline-dialog-card" onClick={e => e.stopPropagation()}>
            <h3 className="offline-dialog-title">
              {pendingOffline ? 'Enable' : 'Disable'} offline alerts
            </h3>
            <p className="offline-dialog-text">
              Apply this to <strong>all units you have access to</strong>, or just this one?
            </p>
            <div className="offline-dialog-actions">
              <button type="button" className="offline-dialog-btn offline-dialog-btn--primary" disabled={offlineBusy} onClick={() => commitOffline(true)}>
                {offlineBusy ? 'Saving…' : 'Yes, all my units'}
              </button>
              <button type="button" className="offline-dialog-btn" disabled={offlineBusy} onClick={() => commitOffline(false)}>
                Just this unit
              </button>
              <button type="button" className="offline-dialog-btn" disabled={offlineBusy} onClick={() => setPendingOffline(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Layout Grid */}
      <div className="unit-metrics-dashboard">
        {/* Metric Sections Container */}
        <div className="metrics-sections-container">
          {isMobile && (
            <div className="metrics-section-box">
              {waterGapNote && (
                <p className="metric-config-warning" role="status">{waterGapNote}</p>
              )}
              <div className="metrics-cards-grid metrics-cards-grid--water">
                {renderMetricCard(
                  'Water Amount',
                  <>
                    {metrics.waterVolumeLiters !== null ? `${metrics.waterVolumeLiters.toFixed(0)}L` : 'N/A'}
                    <span className="metric-value-slash"> / {volume.total.toLocaleString()}L</span>
                  </>,
                  'water_volume',
                  undefined,
                  true // isLarge
                )}

                {renderMetricCard(
                  'Filled Percentage',
                  filledPercent !== null ? `${filledPercent}%` : 'N/A',
                  'water_volume'
                )}

                {renderMetricCard(
                  'Amount Change',
                  metrics.volumeChangeLiters !== null ? `${metrics.volumeChangeLiters > 0 ? '+' : ''}${metrics.volumeChangeLiters.toFixed(0)} L` : 'N/A',
                  'volume_change',
                  <select 
                    className="volume-window-select" 
                    value={volumeWindow} 
                    onChange={(e) => setVolumeWindow(e.target.value as RollingWindow)}
                  >
                    <option value="24h">Daily</option>
                    <option value="7d">Weekly</option>
                    <option value="30d">Monthly</option>
                  </select>
                )}

                {renderMetricCard(
                  'Water Depth',
                  metrics.waterDepthCm !== null ? `${metrics.waterDepthCm.toFixed(1)} cm` : 'N/A',
                  'water_depth'
                )}
              </div>
            </div>
          )}

          <div className="metrics-section-box">
            <div className="metrics-cards-grid metrics-cards-grid--battery">
              {renderMetricCard(
                'Battery Percentage',
                metrics.batteryCapacityPercent !== null ? `${metrics.batteryCapacityPercent}%` : 'N/A',
                'battery_capacity',
                undefined,
                true // isLarge
              )}

              {renderMetricCard(
                'Battery Voltage',
                currentVoltage !== null ? `${currentVoltage.toFixed(2)} V` : 'N/A',
                'voltage'
              )}

              {renderMetricCard(
                'Charge / Discharge Rate',
                metrics.batteryChargeRateVperHr !== null
                  ? `${metrics.batteryChargeRateVperHr > 0 ? '+' : ''}${metrics.batteryChargeRateVperHr.toFixed(3)} V/hr`
                  : 'N/A',
                'charge_rate'
              )}
            </div>
          </div>
        </div>

        {/* Telemetry Chart Section */}
        <div className="chart-section-box-full">
          <div className="chart-header-row">
            <div className="chart-controls-left">
              <select
                className="chart-control-select"
                value={selectedChartMetric}
                onChange={(e) => setSelectedChartMetric(e.target.value)}
              >
                {chartMetricOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <select
                className="chart-control-select"
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value as any)}
              >
                <option value="24h">24 Hours</option>
                <option value="7d">7 Days</option>
                <option value="30d">30 Days</option>
                <option value="3m">3 Months</option>
                <option value="1y">1 Year</option>
              </select>
            </div>

            <div className="chart-timeframe-navigator">
              <button
                type="button"
                className="interval-nav-btn"
                aria-label="Previous time period"
                title="Previous time period"
                onClick={handlePrevInterval}
              >
                <span aria-hidden="true">&lt;</span>
              </button>
              {/* Split so a narrow viewport wraps between the two timestamps
                  instead of through the middle of a date. */}
              <span className="interval-display-text" title={timeframeText}>
                <span className="interval-date">{intervalStartText}</span>
                <span className="interval-date-dash" aria-hidden="true">–</span>
                <span className="interval-date">{intervalEndText}</span>
              </span>
              <button
                type="button"
                className="interval-nav-btn"
                aria-label="Next time period"
                title="Next time period"
                onClick={handleNextInterval}
                disabled={isLatest}
              >
                <span aria-hidden="true">&gt;</span>
              </button>
            </div>

            <div className="chart-controls-right-spacer" />
          </div>

          <div className="chart-container-box">
            {telemetryLoading ? (
              <div style={{ padding: '2rem', textAlign: 'center' }}>Loading telemetry...</div>
            ) : (
              <HistoricChart
                data={chartData}
                selectedMetric={selectedChartMetric}
                metricLabel={chartMetricLabel}
                metricUnit={chartMetricUnit}
                timeframe={timeframe}
                domain={[intervalStart.getTime(), intervalEnd.getTime()]}
              />
            )}
          </div>
        </div>
      </div>

      {alertConfigOpen && (
        <AlertConfigDialog
          unitId={activeUnit.unitId}
          unitName={activeUnit.nodeName}
          metric={alertConfigOpen.metric}
          metricLabel={alertConfigOpen.label}
          totalVolume={volume.total}
          onClose={() => setAlertConfigOpen(null)}
        />
      )}
    </div>
  );
}

export default UnitData;
